import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Form } from "react-bootstrap";
import { AiOutlineReload } from "react-icons/ai";
import "./captcha.css";

// Ambiguous glyphs (0/O, 1/I/L) are excluded so a correct answer is never
// rejected because of the font.
const CHARS = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
const CODE_LENGTH = 5;
const WIDTH = 150;
const HEIGHT = 48;

const randomCode = () =>
  Array.from(
    { length: CODE_LENGTH },
    () => CHARS[Math.floor(Math.random() * CHARS.length)]
  ).join("");

const draw = (canvas, code) => {
  const ratio = window.devicePixelRatio || 1;
  canvas.width = WIDTH * ratio;
  canvas.height = HEIGHT * ratio;

  const ctx = canvas.getContext("2d");
  ctx.scale(ratio, ratio);

  ctx.fillStyle = "#3B3C40";
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  for (let i = 0; i < 6; i++) {
    ctx.strokeStyle = `rgba(220, 191, 94, ${0.15 + Math.random() * 0.2})`;
    ctx.beginPath();
    ctx.moveTo(Math.random() * WIDTH, Math.random() * HEIGHT);
    ctx.lineTo(Math.random() * WIDTH, Math.random() * HEIGHT);
    ctx.stroke();
  }

  for (let i = 0; i < 40; i++) {
    ctx.fillStyle = `rgba(255, 255, 255, ${Math.random() * 0.3})`;
    ctx.fillRect(Math.random() * WIDTH, Math.random() * HEIGHT, 1.5, 1.5);
  }

  const step = WIDTH / (code.length + 1);
  code.split("").forEach((char, i) => {
    ctx.save();
    ctx.translate(step * (i + 1), HEIGHT / 2);
    ctx.rotate((Math.random() - 0.5) * 0.6);
    ctx.font = `${22 + Math.random() * 6}px ArticulatCF-Bold, sans-serif`;
    ctx.fillStyle = "#dcbf5e";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(char, 0, 0);
    ctx.restore();
  });
};

const Captcha = forwardRef(({ onChange, className = "" }, ref) => {
  const [code, setCode] = useState(randomCode);
  const [value, setValue] = useState("");
  const canvasRef = useRef(null);

  useEffect(() => {
    draw(canvasRef.current, code);
  }, [code]);

  const refresh = () => {
    setCode(randomCode());
    setValue("");
    onChange(false);
  };

  useImperativeHandle(ref, () => ({ reset: refresh }));

  const handleChange = (event) => {
    const next = event.target.value;
    setValue(next);
    onChange(next.toUpperCase() === code);
  };

  return (
    <div className={`captcha-box ${className}`}>
      <div className="captcha-challenge">
        <canvas
          ref={canvasRef}
          className="captcha-canvas"
          style={{ width: WIDTH, height: HEIGHT }}
          role="img"
          aria-label="Captcha challenge. Use the refresh button for a new code."
        />
        <button
          type="button"
          className="captcha-refresh"
          onClick={refresh}
          aria-label="Get a new captcha code"
        >
          <AiOutlineReload />
        </button>
      </div>
      <Form.Control
        required
        value={value}
        onChange={handleChange}
        placeholder="ENTER CODE ABOVE"
        autoComplete="off"
        maxLength={CODE_LENGTH}
        aria-label="Enter the captcha code"
      />
    </div>
  );
});

export default Captcha;
